import { inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { DeviceDetectorService, DeviceInfo } from 'ngx-device-detector';
import { BaseStateService } from '@nebalkon-workspace/base-state';

/**
 * Enumeration of device types.
 */
export enum DeviceType {
  /** Mobile phone device */
  Mobile = 'mobile',
  /** Tablet device */
  Tablet = 'tablet',
  /** Desktop computer */
  Desktop = 'desktop',
}

/**
 * Service for detecting and managing device information and type.
 * 
 * Uses ngx-device-detector to identify the user's device and provides reactive
 * state management for device information. Extends BaseStateService to store
 * detailed device info while also providing a simplified device type signal.
 * 
 * @example
 * ```typescript
 * // In a component
 * readonly deviceService = inject(DeviceTypeService);
 * 
 * ngOnInit() {
 *   this.deviceService.defineDeviceData();
 *   
 *   const deviceType = this.deviceService.deviceType();
 *   const deviceInfo = this.deviceService.value();
 *   
 *   if (deviceType === DeviceType.Mobile) {
 *     // Show mobile-optimized UI
 *   }
 * }
 * ```
 */
@Injectable({ providedIn: 'root' })
export class DeviceTypeService extends BaseStateService<DeviceInfo> {
  readonly #deviceDetector = inject(DeviceDetectorService);

  /**
   * Signal containing the simplified device type (mobile, tablet, or desktop).
   */
  readonly deviceType = signal<DeviceType | null>(null);

  private readonly _isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  /**
   * Detects and sets both detailed device information and simplified device type.
   * Should be called during application initialization.
   */
  public defineDeviceData(): void {
    if (!this._isBrowser) return;

    this.defineDeviceInfo();
    this.defineDeviceType();
  }

  /**
   * Determines the simplified device type (mobile, tablet, or desktop).
   */
  private defineDeviceType(): void {
    const isMobile = this.#deviceDetector.isMobile();

    if (isMobile) {
      this.deviceType.set(DeviceType.Mobile);
      return;
    }

    const isTablet = this.#deviceDetector.isTablet();

    if (isTablet) {
      this.deviceType.set(DeviceType.Tablet);
      return;
    }

    this.deviceType.set(DeviceType.Desktop);
  }

  /**
   * Retrieves and stores detailed device information.
   */
  private defineDeviceInfo(): void {
    this.setValue(this.#deviceDetector.getDeviceInfo());
  }
}
